import HighchartsReact from "highcharts-react-official";
import Highcharts from "highcharts";
import { useEffect, useRef, useState } from "react";
import ArcGrid, { GridColumn } from "app/components/arc/ArcGrid";
import { MhdlgService } from "app/services/cp/MhdlgService";
import { ResponseResultCode } from "app/model/response-api";
import { toast } from "react-toastify";
import { DocuBizPlnd } from "app/model/cp/DocuBizPlnd";
import moment from "moment";
import { useNavigate } from "react-router-dom";
import DashboardLoading from "./DashboardLoading";
import DashboardNoData from "./DashboardNoData";
import {HighchartsOptionsUtil} from "app/utils/highchartsOptionsUtil";
import NoDataToDisplay from "highcharts/modules/no-data-to-display";
import DashboardCard from "./DashboardCard";
import {DashboardModuleProps} from "app/model/db/dashboardModuleProps";
NoDataToDisplay(Highcharts);

const graph: any = {
  chart: {
    height: 270,
    spacingTop: 0,
    spacingBottom: 50,
    spacingLeft: 0,
    spacingRight: 0,
    backgroundColor: null
  },
  colors: ['#4C96F9', '#44C5DB'],
  title: {
    text: ''
  },
  exporting: { enabled: false },
  credits: { enabled: false },
  noData: HighchartsOptionsUtil.noData,
  legend: {
    layout: 'horizontal',
    align: 'center',
    verticalAlign: 'bottom',
    y: 20,
    borderWidth: 0,
    symbolHeight: 10,
    symbolPadding: 5,
    itemDistance: 20,
    labelFormat: `<span style="font-size: 0.75rem;">{name} <span style="font-size: 0.75rem; font-weight: 600;">{y}</span>건</span>`,
  },
  subtitle: {
    align: 'center',
    verticalAlign: 'middle',
    useHTML: true,
  },
  tooltip: {
    formatter: function (this: any) {
      return `<span style="color: ${this.color};">●&nbsp;</span>${this.point.name}: <strong style="font-size: 0.75rem;">${this.y}</strong>건`;
    },
    style: {
      fontSize: '0.75rem'
    }
  },
  plotOptions: {
    pie: {
      allowPointSelect: true,
      cursor: 'pointer',
      innerSize: "70%",
      borderRadius: 0,
    },
    series: {
      showInLegend: true,
      borderWidth: 0,
      dataLabels: {
        enabled: false,
      },
    },
  },
  series: [],
  responsive: {
    rules: [{
      condition: {
        maxWidth: 500,
      },
      chartOptions: {
        noData: HighchartsOptionsUtil.noDataResponsive,
      }
    }]
  }
};

const columns: GridColumn[] = [
  {
    header: '문서명',
    name: 'docId',
    align: "left",
    minWidth: 0,
    width: 0,
    hidden: true
  },
  {
    header: '프로젝트명',
    name: 'bizNm',
    align: "left",
    minWidth: 200,
    formatter: function (e: any) {
      return `
        <div class="text-ellipsis" style="max-width:220px;">
          ${e.value}
        </div>
      `;
    }
  },
  {
    header: '기간',
    name: 'bizPeriod',
    align: 'center',
    minWidth: 180,
    width: 200,
  },
  {
    header: '책임자',
    name: 'bizManagerNm',
    align: 'center',
    minWidth: 100,
    width: 100,
  },
  {
    header: '상태',
    name: 'bizStatusCd',
    align: 'center',
    minWidth: 80,
    formatter: function (e: any) {
      return e.value === 'C' ? '완료' : '진행';
    },
  },
];

Highcharts.setOptions({
  ...HighchartsOptionsUtil.locale.kr.globalOptions,
  title: { text: '' },
  exporting: { enabled: false },
  credits: { enabled: false },
  tooltip: { shared: true, headerFormat: '' },
  accessibility: { enabled: false },
});

export default function ProjectMng(props: Readonly<DashboardModuleProps>) {
  const gridRef: any = useRef<typeof ArcGrid>();
  const navigate = useNavigate();
  const [chart, setChart] = useState<any>();
  const [data, setData] = useState<any[]>();
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let data: any[] = [];
    MhdlgService.getInstance().getDetailList().then(response => {
      if (response && response.resultCode === ResponseResultCode.Y && response.data && Array.isArray(response.data)) {
        response.data.forEach((d: DocuBizPlnd) => {
          const bizStartDt = moment(d.bizStartYmd, 'YYYY-MM-DD');
          const bizPeriodYear = Number(d.bizPeriodYear?.replace(/[^0-9.]/g, ''));
          const bizEndDt = moment(bizStartDt).add(bizPeriodYear, 'years');
          data.push({
            docId: d.docId,
            bizNm: d.bizNm,
            bizStartYmd: d.bizStartYmd,
            bizEndYmd: bizEndDt.format('YYYY-MM-DD'),
            bizPeriod: `${bizStartDt.format('YY.MM.DD')} ~ ${bizEndDt.format('YY.MM.DD')}`,
            bizStatusCd: `${moment().diff(bizEndDt) > 0 ? 'C' : 'P'}`,
            bizManagerNm: d.bizManagerNm ?? '-',
          });
        });
      } else {
        toast.error('프로젝트 관리 데이터를 불러오지 못했습니다.')
      }
    }).catch(() => {
      toast.error('프로젝트 관리 데이터를 불러오는 중 오류가 발생했습니다.');
    }).finally(() => {
      setData(data);
      setIsLoading(false);
    });
  }, []);

  function getChart() {
    let seriesData: any[] = [];
    let subtitle = {};

    if (data && data.length > 0) {
      seriesData = [
        {
          name: '완료',
          y: data?.filter((i: any) => i.bizStatusCd === 'C').length
        }, {
          name: '진행',
          y: data?.filter((i: any) => i.bizStatusCd === 'P').length
        }
      ];

      subtitle = {
        align: 'center',
        verticalAlign: 'middle',
        y: 5,
        useHTML: true,
        text:
            `<div style="display: flex; flex-direction: column; justify-content: center; align-items: center;">
          <div style="color: #555; font-size: 0.75rem; font-weight: 500;">총 프로젝트</div>
          <div style="color: #333; font-size: 1.2rem; font-weight: 800;">${data?.length ?? 0}건</div>
        </div>`,
      }
    }

    setChart({
      ...graph,
      subtitle: subtitle,
      series: [{
        name: '프로젝트 상태별 건수',
        type: 'pie',
        data: seriesData
      }]
    });
  }

  useEffect(() => {
    if (data) {
      getChart();
    }
  }, [data]);

  function handleClickGridRow(data: any) {
    if (data && gridRef?.current?.getInstance()) {
      const rowKey = data.rowKey;
      const rowData = gridRef?.current?.getInstance().getRow(rowKey);
      if (rowData?.docId) {
        navigate(`/cp/reduce/history/business/detail/${rowData.docId}`);
      }
    }
  }

  return (
    <DashboardCard
      title="프로젝트 관리"
      isConfigMode={props.isConfigMode ?? false}
      moreButtonVisible={true}
      moreButtonUrl="/cp/reduce/history"
      dashboardConfig={props.dashboardConfig}
    >
      <DashboardLoading isLoading={isLoading} />
      {!data || data?.length === 0 ? (
        <DashboardNoData
          message="진행 중인 배출권거래제 외부사업이 없습니다."
          link={{text: '외부사업 신청하기', to: '/cp/reduce/methodology'}}
        />
      ) : (
        <div className="graph-table-col1" style={{height:'100%'}}>
          <div className="graph-a">
            <div className="chart">
              { chart && (
                <HighchartsReact highcharts={Highcharts} options={chart}/>
              )}
            </div>
          </div>
          <div>
            <ArcGrid
              gridRef={gridRef}
              columns={columns}
              useStaticData={true}
              data={data}
              onClickRow={handleClickGridRow}
              scrollY={true}
              scrollX={true}
            />
          </div>
        </div>
      )}
    </DashboardCard>
  );
}